import { nanoid } from 'nanoid'

import { CreatorType, AttachmentType, PermissionLevel, GroupType } from '@/constants'
import { Group } from '@/common/interfaces'
import { ParcelType } from '@/constants'
import useCommonStore from '@/composables/common'

/* DIALOG */

export enum DialogMessageType {
  Information = 'Information',
  Success = 'Success',
  Warning = 'Warning',
  Error = 'Error',
}

export interface DialogMessage {
  title: string
  message: string
  duration: number
  type: DialogMessageType
  timestamp: Date
  callback: () => void | null
}

export interface IURL {
  redirectURI: string
}
export interface IUser {
  _id: string
  status: {
    lastLogin: string | Date
    created: string | Date
    browserLanguage: string
    isRegistered: boolean
  }
  profile: {
    username: string
    fullName: string
    provider: string // Dataporten or Canvas etc.?
    email: string
    language: string
    organization: string
    // Canvas courses ,zoom meeting IDs /dataporten groups the user is a member of
    groups: Group[]
  }
  mqtt: {
    username: string
    password: string
  }
}
export class User implements IUser {
  _id = ''
  status: {
    lastLogin: Date
    created: Date
    browserLanguage: string
    isRegistered: boolean
  }
  profile: {
    username: string
    fullName: string
    provider: string // Dataporten or Canvas etc.?
    email: string
    language: string
    organization: string
    // Canvas courses ,zoom meeting IDs /dataporten groups the user is a member of
    groups: Group[]
  }
  mqtt: {
    username: string
    password: string
  }

  constructor(spec: IUser) {
    this._id = spec._id
    this.status = {
      created: new Date(spec.status.created),
      lastLogin: new Date(spec.status.lastLogin),
      browserLanguage: spec.status.browserLanguage,
      isRegistered: spec.status.isRegistered,
    }
    this.profile = {
      username: spec.profile.username,
      fullName: spec.profile.fullName,
      provider: spec.profile.provider,
      email: spec.profile.email,
      language: spec.profile.language,
      organization: spec.profile.organization,
      // Canvas courses ,zoom meeting IDs /dataporten groups the user is a member of
      groups: spec.profile.groups || [],
    }
    this.mqtt = {
      username: spec.mqtt.username || this._id, // default
      password: spec.mqtt.password || this._id.split('').reverse().join(''), //default
    }
  }
}

export interface BoardEntry {
  xpos: number
  ypos: number
  highlightColor: string
  wall_id: string
  creator: Creator
}
export interface IWallDetails {
  pin: string
  lastPinAllocation: number
  permaLink: string
  canvasCourseID: string
  title: string
  description: string
  subjects: ISubject[]
  notes: number
  contributors: string[]
}
export interface IWallStatus {
  created: Date | string
  lastOpened: Date | string
  lastContribution: Date | string
  pin: boolean
  permaLink: boolean
  readOnly: boolean
  deleted: boolean
  enableTracking: boolean
}
export interface ISharing {
  _id: string
  created: Date | string
  access: boolean
  group_id: string
  groupType: GroupType
  permissionLevel: PermissionLevel
}
export interface IWallUsers {
  owner: Creator
  shares: ISharing[]
}

export interface ISubject {
  _id: string
  name: string
  icon: string
}

export class Subject implements ISubject {
  _id: string
  name: string
  icon: string

  constructor(subject: ISubject | Subject) {
    this._id = subject._id
    this.name = subject.name
    this.icon = subject.icon
  }
}

export interface IWall {
  _id?: string
  details?: Partial<IWallDetails> // Partial makes all properties in T optional
  status?: Partial<IWallStatus>
  users?: Partial<IWallUsers>
  tasks?: Task[]
  boards?: Board[]
  preview?: PreviewNote[]
}

// Let's try a FP variant!
/* const Wall = (w: Wall, u?: User) => {
  const obj = {

  }
  return obj
} */

export class Wall implements IWall {
  _id = nanoid()
  details = {
    pin: '',
    lastPinAllocation: 0,
    permaLink: '',
    canvasCourseID: '',
    title: '',
    description: '',
    subjects: [] as ISubject[],
    notes: 0,
    contributors: [''],
  }
  status = {
    created: new Date(),
    lastOpened: new Date(),
    lastContribution: new Date(),
    pin: false,
    permaLink: false,
    readOnly: false,
    deleted: false,
    enableTracking: true,
  }
  users: { owner: Creator; shares: Sharing[] } = {
    owner: {
      type: CreatorType.ANONYMOUS,
      username: '',
      user_id: '',
    },
    shares: [] as Sharing[],
  }
  tasks: Task[] = []
  boards: Board[] = []
  preview: PreviewNote[] = []

  get authorisedAccessOnly() {
    return !(this.status.pin || this.status.permaLink)
  }

  update(wall: IWall) {
    if (wall._id) this._id = wall._id
    if (wall.details) this.details = { ...this.details, ...wall.details }
    if (wall.status) {
      this.status = {
        ...this.status,
        ...wall.status,
        created: new Date(wall.status.created || ''),
        lastOpened: new Date(wall.status.lastOpened || ''),
        lastContribution: new Date(wall.status.lastContribution || ''),
      }
      // Prefer these properties to be Date type
      if (wall.status.created) this.status.created = new Date(wall.status.created)
      if (wall.status.lastOpened) this.status.lastOpened = new Date(wall.status.lastOpened)
      if (wall.status.lastContribution) this.status.lastContribution = new Date(wall.status.lastContribution)
    }
    if (wall.users) {
      let shares: Sharing[] = []
      if (wall.users.shares) shares = wall.users.shares.map((s) => new Sharing(s))
      this.users = { ...this.users, ...wall.users, shares }
    }
    if (wall.tasks) {
      this.tasks = wall.tasks.map((t) => ({
        _id: t._id,
        title: t.title,
        status: { created: new Date(t.status.created), deleted: t.status.deleted },
        order: t.order,
        allowBoardCreation: t.allowBoardCreation,
      }))
    }
    if (wall.boards) {
      this.boards = wall.boards.map((b) => ({
        _id: b._id,
        name: b.name,
        createdAt: b.createdAt,
        updatedAt: b.updatedAt,
        active: false,
      }))
    }
    if (wall.preview) {
      this.preview = wall.preview.map((p) => ({
        note_id: p.note_id,
        attachment: p.attachment,
        text: p.text,
        tags: p.tags,
        added: p.added,
        highlightColor: p.highlightColor,
        username: p.username,
      }))
    }
  }

  constructor(wall: IWall) {
    const common = useCommonStore()
    this.update(wall)
    const boardName = common.getters.languageCode.value.toLowerCase().includes('no') ? 'Felles' : 'Shared'
    if (this.boards.length === 0) this.addBoard({ _id: nanoid(), name: boardName, active: false })
  }

  setTitle(title: string): void {
    this.details.title = title
  }

  prepareDuplication(copyTag: string) {
    this.status.created = new Date()
    this.details.title = `${copyTag} ${this.details.title.toLowerCase()}`

    // reset sharing details
    this.users.shares = []
    this.details.permaLink = ''
    this.status.permaLink = false
    this.details.pin = ''
    this.status.pin = false
  }

  setDescription(desc: string): void {
    this.details.description = desc
  }

  addSubject(subj: Subject): void {
    if (this.details.subjects.findIndex((s: Subject) => s.name === subj.name) === -1) this.details.subjects.push(subj)
  }

  addNotesCount() {
    this.details.notes++
  }

  deductNotesCount() {
    if (this.details.notes - 1 >= 0) {
      this.details.notes--
    }
  }

  addContributor(contr: string) {
    if (this.details.contributors.findIndex((c: string) => c === contr) !== -1) {
      this.details.contributors.push(contr)
    }
  }

  removeContributor(contr: string) {
    const index = this.details.contributors.indexOf(contr)
    this.details.contributors.splice(index, 1)
  }

  assignNewOwner(user: User) {
    this.users.owner.user_id = user._id
    this.users.owner.type = CreatorType.REGISTERED
    this.users.owner.username = user.profile.username
  }

  // TASKS

  addTask(task: Task) {
    this.tasks.push(task)
  }

  removeTask(id: string) {
    const index = this.tasks.findIndex((t) => t._id === id)
    this.tasks[index].status.deleted = true
  }

  getTask(id: string): Task | void {
    const result = this.tasks.find((t) => t._id === id)
    if (result !== undefined) {
      return result as Task
    }
  }

  // STATUS

  setLastOpened() {
    this.status.lastOpened = new Date()
  }

  setLastContribution() {
    this.status.lastOpened = new Date()
  }

  setReadOnly(rdnl: boolean) {
    this.status.readOnly = rdnl
  }

  setDeleted(delt: boolean) {
    this.status.deleted = delt
  }

  setEnableTracking(nbltr: boolean) {
    this.status.enableTracking = nbltr
  }

  addSharing(shrng: Sharing) {
    this.users.shares.push(shrng)
  }

  removeSharing(id: string) {
    const index = this.users.shares.findIndex((s) => s._id === id)
    this.users.shares.splice(index, 1)
  }

  // BOARD

  addBoard(board: Board) {
    this.boards.push(board)
  }

  getBoardById(id: string) {
    const index = this.boards.findIndex((b) => b._id === id)
    return this.boards[index]
  }

  // PREVIEW

  addPreview(note: PreviewNote) {
    if (this.preview.length < 4) this.preview.push(note)
  }
}
export interface Attachment {
  type: AttachmentType
  title: string
  description: string
  url: string
  previewImage?: string // LEGACY
}

export interface Origin {
  creator: Creator
  noteId: string
  text: string
}

export interface Edit {
  creator: Creator
  date: Date
  text: string
}

export interface Task {
  _id: string
  title: string
  status: {
    created: Date
    deleted: boolean
  }
  order: number
  allowBoardCreation: boolean
}

export interface Creator {
  type: CreatorType
  username: string
  user_id: string
}

export interface Board {
  _id?: string
  name: string
  createdAt?: Date
  updatedAt?: Date

  active: boolean // Used for front-end
}
export interface INoteStatus {
  created?: Date | string
  createdBy?: Creator
  deleted?: boolean
  isHighlight?: boolean
}

export interface PreviewNote {
  note_id: string
  attachment?: Attachment
  text: string
  tags?: string[]
  added: number
  highlightColor: string
  username: string
}

export interface INote {
  _id?: string
  wall_id?: string
  task_id?: string
  entries?: Record<string, BoardEntry>
  attachment?: Attachment
  text?: string
  status?: Partial<INoteStatus>
  origins?: Origin[]
  edits?: Edit[]
  tags?: string[]
}
export class Note implements INote {
  _id = nanoid()
  wall_id = ''
  task_id = ''
  entries: Record<string, BoardEntry> = {}
  attachment?: Attachment
  text = ''
  status: { created: Date; createdBy: Creator; deleted: boolean; isHighlight: boolean } = {
    created: new Date(),
    createdBy: {
      type: CreatorType.ANONYMOUS,
      username: '',
      user_id: '',
    },
    deleted: false,
    isHighlight: false,
  }
  origins: Origin[] = []
  edits: Edit[] = []
  tags: string[] = []

  // Not part of the DB model, used only to upload the image attachment
  imageData?: string = ''

  update(note: INote) {
    if (note._id) this._id = note._id
    if (note.wall_id) this.wall_id = note.wall_id
    if (note.task_id) this.task_id = note.task_id
    if (note.entries) this.entries = note.entries
    if (note.attachment) {
      this.attachment = {
        type: note.attachment.type,
        title: note.attachment.title,
        description: note.attachment.description,
        url: note.attachment.url,
      }
    }
    // Support legacy 'previewImage'  - translate it to the url attribute
    // OLD:
    //    url: string // For 'EXTERNAL' attachments, this stores the URL to be scraped. For 'IMAGE' attachments, it stores the S3 image URL
    //    previewImage?: string // For 'EXTERNAL' attachments, this stores the scraped image preview URL. For 'IMAGE" attachments, it is empty
    // NEW:
    //    url: string // For 'EXTERNAL' attachments, this stores the website's **scraped image URL**. For 'IMAGE' attachments, it stores the **S3 image URL**
    if (this.attachment && this.attachment.type == AttachmentType.external && note.attachment?.previewImage) {
      this.attachment.url = note.attachment.previewImage
    }
    if (note.text) this.text = note.text
    if (note.status) {
      this.status = { ...this.status, ...note.status, created: note.status.created ? new Date(note.status.created) : new Date() }
    }
    if (note.origins) {
      this.origins = note.origins.map((o) => ({
        creator: o.creator,
        noteId: o.noteId,
        text: o.text,
      }))
    }
    if (note.edits) {
      this.edits = note.edits.map((e) => ({
        creator: e.creator,
        date: new Date(e.date),
        text: e.text,
      }))
    }
    if (note.tags) this.tags = Array.from(note.tags)
  }

  constructor(note: INote) {
    this.update(note)
    this.extractTags(this.text)
  }

  setHighlightColor(color: string, wallId: string) {
    this.entries[wallId].highlightColor = color
  }

  addBoardEntry(boardId: string, entry: BoardEntry) {
    this.entries[boardId] = entry
  }

  updateBoardEntry(boardEntry: BoardEntry, pos: number[]) {
    const updatedEntry = boardEntry
    updatedEntry.ypos = pos[0]
    updatedEntry.xpos = pos[1]
    this.entries[boardEntry.wall_id] = updatedEntry
  }

  removeBoardEntry(boardId: string) {
    delete this.entries[boardId]
  }

  resetBoardEntries() {
    this.entries = {}
  }

  addEdit(text: string, creator: Creator) {
    this.text = text
    this.edits.push({
      creator: creator,
      date: new Date(),
      text: text,
    })

    this.tags = []
    this.extractTags(text)
  }

  addOrigin(origin: Origin) {
    this.origins.push(origin)
  }

  extractTags(text: string) {
    for (const word of text.split(' ')) {
      if (word.includes('#') && word.length > 1) {
        const index = word.indexOf('#')
        const tag = word.substring(index + 1, word.length)
        if (!this.tags.includes(tag)) {
          this.tags.push(tag)
        }
      }
    }
  }
}

export class Sharing implements ISharing {
  _id: string
  created: Date | string
  access: boolean
  group_id: string
  groupType: GroupType // NOTE: do we really need this?
  permissionLevel: PermissionLevel

  constructor(sharing: ISharing) {
    this._id = sharing._id || nanoid()
    this.created = new Date(sharing.created)
    this.access = sharing.access || true
    this.group_id = sharing.group_id || ''
    this.groupType = sharing.groupType || GroupType.DATAPORTEN
    this.permissionLevel = sharing.permissionLevel || PermissionLevel.Join
  }

  setPermissionLevel(permissionLevel: PermissionLevel) {
    this.permissionLevel = permissionLevel
  }
}

export interface SubscriptionData {
  wall_id?: string
  user?: {
    id: string
    username: string
    isRegistered: boolean
    isOwner: boolean
  }
  status?: {
    authorisedAccessOnly: boolean
    readOnly: boolean
    connections?: {
      owners: string[]
      authorised: string[]
      anonymous: string[]
    }
  }
}
export interface IParcel {
  parcelType: ParcelType
  _id?: string
  subscription?: SubscriptionData
  body?: Wall | Task[] | Note | string
}
export class Parcel implements IParcel {
  _id = ''
  parcelType = ParcelType.GlobalStatus
  subscription = {
    wall_id: '',
    user: {
      id: '',
      username: '',
      isRegistered: false,
      isOwner: false,
    },
    status: {
      authorisedAccessOnly: true,
      readOnly: false,
      connections: {
        owners: [''],
        authorised: [''],
        anonymous: [''],
      },
    },
  }
  body?: Wall | Task[] | Note | string = '' // wall, task or message object or string (id)

  constructor(spec: IParcel, user?: User, wall?: Wall) {
    this._id = spec._id || nanoid()
    this.parcelType = spec.parcelType // ParcelType enumerator
    if (spec.subscription?.user) this.subscription.user = spec.subscription.user
    else if (user && wall) {
      this.subscription.user = {
        id: user._id,
        username: user.profile.username,
        isRegistered: user.status.isRegistered,
        isOwner: wall.users.owner.user_id === user._id,
      }
    }
    if (spec.subscription?.status) {
      this.subscription.status.authorisedAccessOnly = spec.subscription.status.authorisedAccessOnly
      this.subscription.status.readOnly = spec.subscription.status.readOnly
      if (spec.subscription.status.connections) this.subscription.status.connections = spec.subscription.status.connections
    }
    if (spec.subscription?.wall_id) this.subscription.wall_id = spec.subscription.wall_id
    else if (wall) this.subscription.wall_id = wall._id

    switch (spec.parcelType) {
      case ParcelType.MessageBoard || ParcelType.MessageCreate || ParcelType.MessageDelete || ParcelType.MessageEdited:
        this.body = new Note(spec.body as INote)
        break
      case ParcelType.TaskCreate || ParcelType.TaskDelete || ParcelType.TaskEdited:
        this.body = spec.body as Task[]
        break
      case ParcelType.WallEdited:
        this.body = new Wall(spec.body as Wall)
        break
      default:
        this.body = spec.body as string
    }
  }

  serialised() {
    return JSON.stringify(this)
  }

  toString() {
    const bodyString = JSON.stringify(this.body, null, 4)
    return (
      `PARCEL: ${this.parcelType} | User: ${this.subscription.user.username}` +
      ` | Authorised?: ${this.subscription.user.isRegistered} | User is Owner?: ${this.subscription.user.isOwner}` +
      ` | Body: ${bodyString}`
    )
  }
}
